// Custom-permission grants for tenant users with role "custom".
// Mirrors backend/app/auth/dependencies.py LEVEL_PERMISSIONS/FLAG_PERMISSIONS.

export type PermLevel = "off" | "read" | "write";

export interface PermDef {
  key: string;
  label: string;
  type: "level" | "flag";
  /** When set, the flag/level is only meaningful if the given level perm >= read. */
  requires?: { key: string; level?: Exclude<PermLevel, "off"> };
  /** Only offered when the server has EET enabled (env eet_mode != disabled). */
  eetOnly?: boolean;
}

// `label` is an i18n key under `permDef.*` — translate with t() at render time.
export const PERMISSION_DEFS: PermDef[] = [
  { key: "catalog", label: "permDef.catalog", type: "level" },
  { key: "stock_levels", label: "permDef.stockLevels", type: "flag" },
  { key: "stock_limits", label: "permDef.stockLimits", type: "flag" },
  { key: "stock_notifications", label: "permDef.stockNotifications", type: "flag" },
  { key: "suppliers_write", label: "permDef.suppliersWrite", type: "flag" },
  { key: "stock_receipts", label: "permDef.stockReceipts", type: "flag" },
  { key: "stock_transfers", label: "permDef.stockTransfers", type: "flag" },
  { key: "stock_corrections", label: "permDef.stockCorrections", type: "flag" },
  { key: "stock_counts_view", label: "permDef.stockCountsView", type: "flag" },
  { key: "stock_counts_perform", label: "permDef.stockCountsPerform", type: "flag", requires: { key: "stock_counts_view" } },
  { key: "accounts", label: "permDef.accounts", type: "flag" },
  { key: "cash_ops", label: "permDef.cashOps", type: "flag" },
  { key: "settlements", label: "permDef.settlements", type: "flag" },
  { key: "revenue_overview", label: "permDef.revenueOverview", type: "flag" },
  { key: "exports", label: "permDef.exports", type: "flag" },
  { key: "cash_state", label: "permDef.cashState", type: "flag" },
  { key: "staff", label: "permDef.staff", type: "level" },
  { key: "attendance", label: "permDef.attendance", type: "flag", requires: { key: "staff" } },
  { key: "devices", label: "permDef.devices", type: "flag" },
  { key: "device_registration", label: "permDef.deviceRegistration", type: "flag" },
  { key: "branches", label: "permDef.branches", type: "level" },
  { key: "warehouses", label: "permDef.warehouses", type: "level" },
  { key: "pos_permissions", label: "permDef.posPermissions", type: "level" },
  { key: "eet_history", label: "permDef.eetHistory", type: "flag", eetOnly: true },
  { key: "eet_resend", label: "permDef.eetResend", type: "flag", requires: { key: "eet_history" }, eetOnly: true },
];

export const LEVEL_KEYS = new Set(PERMISSION_DEFS.filter((d) => d.type === "level").map((d) => d.key));

/** Level keys where "read" is implicit for every backoffice role — the
 * editor offers only "Pro čtení"/"Pro zápis" (no "Bez přístupu"). */
export const AUTO_READ_KEYS = new Set(["branches", "warehouses"]);

type PermHolder = { role: string; permissions?: Record<string, unknown> | null };

/** Effective-permission check for UI gating. owner/manager pass everything;
 * custom uses the stored grant map; "write" implies "read". */
export function can(user: PermHolder | null | undefined, key: string, level: Exclude<PermLevel, "off"> = "read"): boolean {
  if (!user) return false;
  if (user.role === "owner" || user.role === "manager") return true;
  const perms = user.permissions || {};
  if (LEVEL_KEYS.has(key)) {
    if (AUTO_READ_KEYS.has(key) && level === "read" && user.role !== "none") return true;
    const v = perms[key];
    return v === "write" || (level === "read" && v === "read");
  }
  return perms[key] === true;
}

/** Normalize a raw stored map to a complete editor state (missing = off). */
export function normalizePerms(perms: Record<string, unknown> | null | undefined): Record<string, PermLevel | boolean> {
  const out: Record<string, PermLevel | boolean> = {};
  for (const def of PERMISSION_DEFS) {
    const v = perms?.[def.key];
    const fallback: PermLevel = AUTO_READ_KEYS.has(def.key) ? "read" : "off";
    out[def.key] = def.type === "level"
      ? (v === "read" || v === "write" ? v : fallback)
      : v === true;
  }
  return out;
}
